import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = (typeof window !== 'undefined' && window.location.hostname && !/^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname)) ? window.location.origin : process.env.REACT_APP_BACKEND_URL;
const VAPID_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const usePushNotifications = () => {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const isSupported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setSupported(isSupported);
    if (isSupported) {
      setPermission(Notification.permission);
      checkSubscription();
    }
  }, []);

  const checkSubscription = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/notifications/status`, { withCredentials: true });
      setSubscribed(res.data.subscribed);
    } catch { /* not logged in yet */ }
  };

  const subscribe = useCallback(async () => {
    if (!supported || !VAPID_KEY) return false;
    setLoading(true);

    try {
      // Register service worker
      const registration = await navigator.serviceWorker.register('/sw-push.js');
      await navigator.serviceWorker.ready;

      // Request permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        setLoading(false);
        return false;
      }

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_KEY),
      });

      // Send to backend
      await axios.post(`${API_URL}/api/notifications/subscribe`, {
        subscription: subscription.toJSON(),
      }, { withCredentials: true });

      setSubscribed(true);
      setLoading(false);
      return true;
    } catch (err) {
      console.error('Push subscribe error:', err);
      setLoading(false);
      return false;
    }
  }, [supported]);

  const unsubscribe = useCallback(async () => {
    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.getRegistration('/sw-push.js');
      if (registration) {
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
          await axios.post(`${API_URL}/api/notifications/unsubscribe`, {
            endpoint: subscription.endpoint,
          }, { withCredentials: true });
        }
      }
      setSubscribed(false);
    } catch (err) {
      console.error('Push unsubscribe error:', err);
    }
    setLoading(false);
  }, []);

  const sendTest = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/api/notifications/test`, {}, { withCredentials: true });
      return true;
    } catch { return false; }
  }, []);

  return { supported, permission, subscribed, loading, subscribe, unsubscribe, sendTest };
};
